import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DataCard, StatusBadge, SkeletonList, QueryError, NotConnected } from '../../../../components/ui';
import { getRecentWins } from '../../../../lib/deal-risk';
import { useBasePath } from '../../../../lib/config/nav-helpers';
import { sees } from '../../../../lib/coverage';

function fmtMoney(cents: number): string {
  const d = cents / 100;
  if (d >= 1_000_000) return `$${(d / 1_000_000).toFixed(1)}M`;
  if (d >= 1_000) return `$${Math.round(d / 1_000)}k`;
  return `$${Math.round(d)}`;
}

/** Deals the CRM closed-won in the last 30 days, plus wins logged by hand. */
export function RecentWinsWidget() {
  const navigate = useNavigate();
  const basePath = useBasePath();
  const recentWins = useQuery({ queryKey: ['recent-wins', 30], queryFn: () => getRecentWins(30), staleTime: 10 * 60_000, retry: 0 });
  const data = recentWins.data;
  const wins = data?.wins ?? [];

  return (
    <DataCard
      title="Recent Wins"
      subtitle={data && wins.length > 0 ? `${wins.length} closed-won in 30 days · ${fmtMoney(data.totalCents)}` : 'Closed-won in your CRM, last 30 days'}
      badge={wins.length > 0 ? <StatusBadge status="success" label={`${wins.length}`} size="xs" /> : undefined}
    >
      {recentWins.isLoading ? <SkeletonList rows={2} /> : recentWins.isError ? <QueryError error={recentWins.error} onRetry={() => recentWins.refetch()} compact /> : !sees(data?.coverage, 'crm') ? (
        <NotConnected need="crm" />
      ) : wins.length === 0 ? (
        <p className="text-2xs text-ink-dim italic">Nothing closed-won in the last 30 days.</p>
      ) : (
        <div className="space-y-1.5">
          {wins.slice(0, 5).map((w) => {
            const href = w.clientId ? `${basePath}/clients/${w.clientId}` : null;
            const when = new Date(w.closed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const row = (
              <>
                <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5" className="mt-0.5 shrink-0"><polyline points="20 6 9 17 4 12" /></svg>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-ink">{w.title}{w.company && w.company !== w.title ? <span className="text-ink-dim"> · {w.company}</span> : null}</p>
                  <p className="text-2xs text-ink-dim">{when}{w.source === 'logged' ? ' · logged in Waypoint' : ''}</p>
                </div>
                {w.amount_cents ? <p className="shrink-0 text-2xs font-mono text-ink">{fmtMoney(w.amount_cents)}</p> : null}
              </>
            );
            return href ? (
              <button type="button" key={w.id} onClick={() => navigate(href)} className="flex w-full items-start gap-2 rounded-lg bg-success-soft/30 px-3 py-2 text-left hover:bg-success-soft/50 transition-colors">
                {row}
              </button>
            ) : (
              <div key={w.id} className="flex items-start gap-2 rounded-lg bg-success-soft/30 px-3 py-2">{row}</div>
            );
          })}
        </div>
      )}
    </DataCard>
  );
}
